import React, { useContext, useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useProjectLineClock } from "../hooks";
import { UserDataContext } from "../app/App";
import {
    Grid,
    Box,
    CircularProgress,
    TextField,
    Typography,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    IconButton,
    AppBar,
    Toolbar,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    Slide,
    Button,
} from "@mui/material";
import { Comment, Fullscreen, Close } from "@mui/icons-material";
import SamsynMap from "../components/custom/SamsynMap";
import LoadingButton from "../components/LoadingButton/LoadingButton";
import Layout from "../components/Layout/Layout";
import ErrorPage from "./ErrorPage";
import { ReportIncidentButton, OpenPhotosButton } from "../components";

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export default function MowingClock() {
    const { id: projectLineId } = useParams();
    const history = useHistory();
    const { user } = useContext(UserDataContext);

    const [issue, setIssue] = useState("");
    const [fullscreen, setFullscreen] = useState(false);

    const {
        startWork,
        stopWork,
        skipWork,
        updateIssue,
        isLoading,
        isLoadingUpdateIssue,
        isLoadingStartProjectLine,
        isLoadingStopProjectLine,
        isLoadingSkipProjectLine,
        isSuccess,
        isError,
        error,
        projectLine,
        accountLocationDescription,
        relatedProjectLines,
        projectLineDetails,
    } = useProjectLineClock({ projectLineId, workType: "mowing" });

    useEffect(() => {
        if (projectLine) {
            setIssue(projectLine.issue ?? "");
        }
    }, [projectLine]);

    if (isLoading) {
        return (
            <Layout color="rgb(75 113 23)" title="Mowing" map={false}>
                <Box display="flex" justifyContent="center">
                    <CircularProgress />
                </Box>
            </Layout>
        );
    }

    if (isError && error?.response?.status === 400) {
        return (
            <Layout color="rgb(75 113 23)" title="Mowing" map={false}>
                <Grid container direction="column" alignItems="center" spacing={2}>
                    <Grid item>
                        <Typography variant="h5">Cannot open mowing line</Typography>
                    </Grid>
                    <Grid item>
                        <Box display="flex" flexDirection="column" alignItems="center">
                            <Typography variant="body1">
                                {error?.response?.data ?? "This mowing line is missing a serial value."}
                            </Typography>
                            <Typography variant="body1">Please choose another line.</Typography>
                        </Box>
                    </Grid>
                    <Grid item>
                        <Button variant="contained" color="primary" onClick={() => history.replace("/mowing")}>
                            Back to Mowing
                        </Button>
                    </Grid>
                </Grid>
            </Layout>
        );
    }

    if (isError) {
        return <ErrorPage />;
    }

    const handleUpdateLine = (actionType) => {
        if (actionType === "start") {
            startWork(user.id, 1);
        } else if (actionType === "stop") {
            stopWork(user.id, 1);
        } else if (actionType === "skip") {
            skipWork(user.id, 1);
        }
    };

    return (
        <>
            <Layout
                color="rgb(75 113 23)"
                title={
                    <Box
                        sx={{
                            whiteSpace: "normal",
                            overflow: "break-word",
                            maxWidth: "100%",
                        }}
                    >
                        {projectLine?.project?.address?.name && projectLine?.product?.name
                            ? `${projectLine?.project?.address?.name} ${projectLine?.product?.name}`
                            : projectLine?.project?.address?.name
                            ? projectLine?.project?.address?.name
                            : projectLine?.project?.name
                            ? projectLine?.project?.name
                            : "Óskráð"}
                    </Box>
                }
                subtitle={user?.name ?? "Mowing"}
                map={
                    projectLine?.project?.address?.latitude && projectLine?.project?.address?.longitude
                        ? { latitude: projectLine?.project?.address?.latitude, longitude: projectLine?.project?.address?.longitude }
                        : false
                }
            >
                <Grid container spacing={3}>
                    {/* Description and Akkord */}
                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography>{`Akkorð: ${`${projectLine?.akkord ? `${projectLine?.akkord} kr.` : ""}`}`}</Typography>
                            <IconButton onClick={() => setFullscreen(true)} aria-label="Full screen" size="medium">
                                <Fullscreen />
                            </IconButton>
                        </Box>
                        <TextField
                            id="outlined-multiline-static"
                            label="Description"
                            multiline
                            minRows={4}
                            value={
                                user.lang === 810360000
                                    ? accountLocationDescription?.description ?? accountLocationDescription?.descriptionEnglish ?? "Óskráð"
                                    : accountLocationDescription?.descriptionEnglish ?? accountLocationDescription?.description ?? "Óskráð"
                            }
                            variant="outlined"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            InputProps={{ readOnly: true }}
                        />
                    </Grid>

                    {/* Issue Text Field */}
                    <Grid item xs={12}>
                        <TextField
                            id="outlined-multiline-static"
                            label="Issue"
                            multiline
                            minRows={4}
                            value={issue}
                            disabled={isLoading || isLoadingUpdateIssue}
                            onChange={(event) => setIssue(event.target.value)}
                            onBlur={() => {
                                if (issue !== projectLine?.issue) {
                                    if (issue === "") {
                                        setIssue(projectLine?.issue ?? "");
                                        return;
                                    }
                                    updateIssue({
                                        ProjectLineId: projectLineId,
                                        Issue: issue,
                                        // TotalInGroup: 1, // Adjust as necessary
                                    });
                                }
                            }}
                            variant="outlined"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>

                    <Grid item xs={6}>
                        <LoadingButton
                            variant="contained"
                            color="primary"
                            fullWidth
                            loading={isLoadingStartProjectLine}
                            disabled={projectLineDetails.hasStartedWork || projectLineDetails.isWorkOver || isLoadingStartProjectLine}
                            onClick={() => handleUpdateLine("start")}
                        >
                            {projectLineDetails.hasStartedWork ? `Started: ${projectLineDetails.startDateFormatted}` : "Start"}
                        </LoadingButton>
                    </Grid>

                    <Grid item xs={6}>
                        <LoadingButton
                            variant="contained"
                            color="primary"
                            fullWidth
                            loading={isLoadingStopProjectLine}
                            disabled={!projectLineDetails.hasStartedWork || projectLineDetails.hasEndedWork || projectLineDetails.isWorkOver || isLoadingStopProjectLine}
                            onClick={() => handleUpdateLine("stop")}
                        >
                            {projectLineDetails.hasEndedWork ? `Stopped: ${projectLineDetails.endDateFormatted}` : "Stop"}
                        </LoadingButton>
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            variant="outlined"
                            label="Time measurement"
                            type="number"
                            value={projectLineDetails?.totalTime ?? 0}
                            fullWidth
                            InputProps={{ readOnly: true }}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <LoadingButton
                            variant="contained"
                            fullWidth
                            color="primary"
                            loading={isLoadingSkipProjectLine}
                            disabled={projectLineDetails.hasStartedWork || projectLineDetails.isWorkOver || isLoadingSkipProjectLine}
                            onClick={() => handleUpdateLine("skip")}
                        >
                            Skip
                        </LoadingButton>
                    </Grid>

                    <Grid item xs={6}>
                        <OpenPhotosButton projectLineId={projectLineId} />
                    </Grid>
                    <Grid item xs={6}>
                        <ReportIncidentButton projectLineId={projectLineId} />
                    </Grid>

                    <Grid item xs={12} style={{ height: 50 + "%" }}>
                        <SamsynMap
                            locationId={projectLine?.project?.address?.id}
                            latitude={projectLine?.project?.address?.latitude}
                            longitude={projectLine?.project?.address?.longitude}
                            serviceType={projectLine?.project?.serviceType?.section}
                        />
                    </Grid>

                    {relatedProjectLines && relatedProjectLines.length > 0 && (
                        <Grid item xs={12}>
                            <Typography variant="h6">Related Issues</Typography>
                            <List dense={true} style={{ maxHeight: "25vh", overflow: "auto" }}>
                                {relatedProjectLines.map((line) => (
                                    <ListItem key={line.id} style={{ width: "100%" }}>
                                        <ListItemIcon>
                                            <Comment />
                                        </ListItemIcon>
                                        <ListItemText primary={line?.issue ?? ""} secondary={line?.workingDateFormatted ?? ""} />
                                    </ListItem>
                                ))}
                            </List>
                        </Grid>
                    )}
                </Grid>
            </Layout>
            <Dialog fullScreen open={fullscreen} onClose={() => setFullscreen(false)} TransitionComponent={Transition} sx={{ padding: 0 }}>
                <DialogContent>
                    <DialogContentText>
                        <AppBar sx={{ position: "relative" }} style={{ backgroundColor: "rgb(75 113 23)" }}>
                            <Toolbar>
                                <IconButton onClick={() => setFullscreen(false)} aria-label="close" edge="start" color="inherit">
                                    <Close />
                                </IconButton>
                                <Typography>{projectLine?.project?.address?.name ?? projectLine?.name ?? ""}</Typography>
                            </Toolbar>
                        </AppBar>
                        <Box sx={{ my: 8, mx: 0 }}>
                            <TextField
                                label="Description"
                                multiline
                                value={
                                    user.lang === 810360000
                                        ? accountLocationDescription?.description ?? accountLocationDescription?.descriptionEnglish ?? "Óskráð"
                                        : accountLocationDescription?.descriptionEnglish ?? accountLocationDescription?.description ?? "Óskráð"
                                }
                                variant="outlined"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                InputProps={{ readOnly: true }}
                            />
                        </Box>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFullscreen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
